// Repertoire browse filters — types, limits, defaults and the production-ready
// query builder. The actual network call that consumes the built query is
// stubbed in `repertoireFilterApi.ts` for now, but everything in this file is
// meant to ship as-is: `buildRepertoireFilterQuery` produces the exact param
// shape the backend filter endpoint is expected to accept.
//
// Until that endpoint exists, `filterRepertoireClientSide` applies the same
// criteria locally over the already-loaded repertoire list.

import { Genre, RepertoireData } from 'Types/RepertoireTypes';
import { editDistance, typoBudget } from 'Utils/FuzzyMatch';

export type RepertoireStatus = 'not_started' | 'in_progress' | 'complete' | 'perfect';

export interface StatusOption {
  id: RepertoireStatus;
  label: string;
  /**
   * When true the pill is shown greyed-out and non-interactive. Used for
   * statuses that have no client-side data source yet ("Perfect" has no stored
   * repertoire-level signal — the only "perfect" concept is the per-loop
   * Perfect Phrase Counter — so it can't be derived here).
   */
  disabled?: boolean;
}

export const STATUS_OPTIONS: StatusOption[] = [
  { id: 'not_started', label: 'Not Started' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'complete', label: 'Complete' },
];

// Level dropdowns run 1..26 inclusive.
export const LEVEL_MIN = 1;
export const LEVEL_MAX = 26;

// Duration slider limits, in seconds: 0:30 .. 20:00.
export const DURATION_MIN = 30;
export const DURATION_MAX = 20 * 60;
export const DURATION_STEP = 5;

// BPM slider limits.
export const BPM_MIN = 30;
export const BPM_MAX = 300;
export const BPM_STEP = 1;

export interface GenreOption {
  /** Representative id for the cluster — used as a stable React key. */
  id: number;
  /**
   * All genre ids that map to this display name — repeats of the same label
   * across ids plus any fuzzily-merged typo variants (see `extractGenres`).
   */
  ids: number[];
  name: string;
}

export interface RepertoireFilters {
  statuses: RepertoireStatus[];
  minLevel: number;
  maxLevel: number;
  genreIds: number[];
  /** [min, max] in seconds */
  durationRange: [number, number];
  /** [min, max] in BPM */
  bpmRange: [number, number];
}

export const defaultFilters: RepertoireFilters = {
  statuses: [],
  minLevel: LEVEL_MIN,
  maxLevel: LEVEL_MAX,
  genreIds: [],
  durationRange: [DURATION_MIN, DURATION_MAX],
  bpmRange: [BPM_MIN, BPM_MAX],
};

/** Query params accepted by the (future) repertoire filter endpoint. */
export interface RepertoireFilterQuery {
  status?: string;
  min_level?: number;
  max_level?: number;
  genre_ids?: string;
  min_duration?: number;
  max_duration?: number;
  min_bpm?: number;
  max_bpm?: number;
}

/**
 * Convert UI filter state into backend query params. Defaults (full ranges,
 * empty multi-selects) are omitted so the request only carries meaningful
 * constraints. This is the prod-ready contract — the dummy API just reads it.
 */
export function buildRepertoireFilterQuery(filters: RepertoireFilters): RepertoireFilterQuery {
  const query: RepertoireFilterQuery = {};

  if (filters.statuses.length > 0) {
    query.status = filters.statuses.join(',');
  }

  if (filters.minLevel > LEVEL_MIN) {
    query.min_level = filters.minLevel;
  }
  if (filters.maxLevel < LEVEL_MAX) {
    query.max_level = filters.maxLevel;
  }

  if (filters.genreIds.length > 0) {
    query.genre_ids = filters.genreIds.join(',');
  }

  const [durMin, durMax] = filters.durationRange;
  if (durMin > DURATION_MIN) {
    query.min_duration = durMin;
  }
  if (durMax < DURATION_MAX) {
    query.max_duration = durMax;
  }

  const [bpmMin, bpmMax] = filters.bpmRange;
  if (bpmMin > BPM_MIN) {
    query.min_bpm = bpmMin;
  }
  if (bpmMax < BPM_MAX) {
    query.max_bpm = bpmMax;
  }

  return query;
}

/** True when any filter deviates from its default (used for the Clear control / badge). */
export function isFilterActive(filters: RepertoireFilters): boolean {
  return Object.keys(buildRepertoireFilterQuery(filters)).length > 0;
}

/** Format a duration in seconds as m:ss. */
export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * True when two normalized genre names are close enough to be the same genre.
 * The typo tolerance (via {@link typoBudget}) is judged by the shorter of the
 * two lengths and stays strict: exact-only on short names where a one-character
 * change usually means a genuinely different word (e.g. "Folk" vs "Funk"), up to
 * two characters on longer names where typos are more likely (e.g. "Classical"
 * vs "Classicsal").
 */
function isSameGenreName(a: string, b: string): boolean {
  const budget = typoBudget(Math.min(a.length, b.length));
  if (budget === 0) return a === b;
  if (Math.abs(a.length - b.length) > budget) return false;
  return editDistance(a, b) <= budget;
}

/**
 * Build the genre dropdown options from the loaded repertoire list: walk every
 * piece, collect its genres and dedupe by name, then sort alphabetically. Called
 * once the browse list loads so the genre selections reflect what's actually
 * present.
 *
 * Deduping is two-stage. First we collapse exact (case/whitespace-insensitive)
 * duplicates — the same label recurs under many different ids in the data —
 * counting occurrences. Then we fuzzily merge near-identical spellings so typos
 * fold into the canonical genre ("Classicsal" → "Classical"). Merging processes
 * the most common spelling first so the canonical (correctly-spelled, usually
 * more frequent) label wins as the display name rather than the typo.
 *
 * Each option keeps the *set* of ids that share that genre so filtering can match
 * any piece tagged with any of them, including the typo'd variants.
 */
export function extractGenres(pieces: RepertoireData[]): GenreOption[] {
  const byExactName = new Map<
    string,
    { key: string; name: string; ids: number[]; count: number }
  >();
  for (const piece of pieces) {
    for (const genre of piece.genres ?? []) {
      if (!genre || genre.id == null || !genre.name) continue;
      const name = genre.name.trim();
      const key = name.toLowerCase();
      if (!key) continue;
      const existing = byExactName.get(key);
      if (existing) {
        existing.count += 1;
        if (!existing.ids.includes(genre.id)) existing.ids.push(genre.id);
      } else {
        byExactName.set(key, { key, name, ids: [genre.id], count: 1 });
      }
    }
  }

  const clusters: { key: string; name: string; ids: number[] }[] = [];
  const byFrequency = Array.from(byExactName.values()).sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name),
  );
  for (const group of byFrequency) {
    const match = clusters.find((cluster) => isSameGenreName(cluster.key, group.key));
    if (match) {
      for (const id of group.ids) if (!match.ids.includes(id)) match.ids.push(id);
    } else {
      clusters.push({ key: group.key, name: group.name, ids: [...group.ids] });
    }
  }

  return clusters
    .map((cluster) => ({ id: cluster.ids[0], ids: cluster.ids, name: cluster.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Derive a piece's browse status from the user's completion percentage,
 * mirroring the logic in `RepertoireItem` (percent_complete ≥ 100 ⇒ complete,
 * > 0 ⇒ in progress, else not started).
 *
 * Note this never returns `'perfect'`: there's no stored repertoire-level
 * "perfected" signal to derive it from, so that status is disabled in the UI.
 */
export function derivePieceStatus(percentComplete: number | null | undefined): RepertoireStatus {
  const pct = percentComplete ?? 0;
  if (pct >= 100) return 'complete';
  if (pct > 0) return 'in_progress';
  return 'not_started';
}

/**
 * Temporary client-side filtering, standing in for the backend filter endpoint.
 * Applies status, genre, level and BPM criteria:
 *  - Status: a piece matches if its derived status (from `statusByRepertoireId`,
 *    defaulting to `'not_started'` for pieces with no progress record) is one of
 *    the selected statuses. No status selected ⇒ no status constraint.
 *  - Genre: a piece matches if it has at least one of the selected genres
 *    (no genre selected ⇒ no genre constraint).
 *  - Level: inclusive [minLevel, maxLevel] over the piece's level_number.
 *  - BPM: inclusive [min, max].
 * Duration is intentionally not applied yet (its runtime math comes later).
 * Pieces missing a level/bpm value are not excluded by that particular criterion.
 */
export function filterRepertoireClientSide(
  pieces: RepertoireData[],
  filters: RepertoireFilters,
  statusByRepertoireId?: Map<number, RepertoireStatus>,
): RepertoireData[] {
  return pieces.filter((piece) => {
    // Status — piece's derived progress status must be one of those selected.
    if (filters.statuses.length > 0) {
      const status = statusByRepertoireId?.get(piece.id) ?? 'not_started';
      if (!filters.statuses.includes(status)) {
        return false;
      }
    }

    // Level — inclusive range; skip pieces with no known level rather than drop them.
    const level = piece.level?.level_number;
    if (level != null && (level < filters.minLevel || level > filters.maxLevel)) {
      return false;
    }

    // BPM — inclusive range; skip when the piece has no bpm.
    const bpm = piece.bpm;
    if (bpm != null && bpm > 0 && (bpm < filters.bpmRange[0] || bpm > filters.bpmRange[1])) {
      return false;
    }

    // Genre — at least one selected genre present.
    if (filters.genreIds.length > 0) {
      const ids = new Set((piece.genres ?? []).map((g: Genre) => g.id));
      if (!filters.genreIds.some((id) => ids.has(id))) {
        return false;
      }
    }

    return true;
  });
}
